import React, { useState, useEffect, useCallback } from "react";
import { Suspense } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import CanvasPageRenderer from "../../../Canvas/components/CanvasPageRenderer";
import { pingTask } from "../../../../util/apiService";
import { processCanvasJsonWithPartDetails } from "../../../../util/canvas/processCanvasJsonWithPartDetails";
import { refreshCanvasPagesWithLatestParts } from "../../../../util/canvas/refreshCanvasPartsWithSearch";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { SharedHistoryContext } from "../../../../common/RichTextEditor/LexicalEditor/context/SharedHistoryContext";
import { TableContext } from "../../../../common/RichTextEditor/LexicalEditor/plugins/TablePlugin";
import { ToolbarContext } from "../../../../common/RichTextEditor/LexicalEditor/context/ToolbarContext";
import Editor from "../../../../common/RichTextEditor/Editor/Editor";
import { buildImportMap } from "../../../../util/lexical";
import PlaygroundNodes from "../../../../common/RichTextEditor/LexicalEditor/nodes/PlaygroundNodes";
import PlaygroundEditorTheme from "../../../../common/RichTextEditor/LexicalEditor/themes/PlaygroundEditorTheme";
import {
  NODE_SELECTION_BLUE,
  isDescendantLockedPreview,
  isAuditPreviewFocusedLeafCheckbox,
  getDescendantLockedCheckboxStyle,
  getHaloWrapperStyle,
  getAuditPreviewReadonlyHaloCheckboxStyle,
  auditPreviewSectionDescendantWrapStyle,
} from "./nodeSelectionCheckboxStyles";

const CanvasNode = ({
  node,
  nodeNumber,
  parentContext,
  nodeType,
  onNavigate,
  checkboxState,
  disabled = false,
  showCheckbox = true,
  showHeading = null,
  showShortDescription = true,
  checkboxLabelColor = null,
  isLocked = false,
  isRevisionLocked = false,
  enableStagePopup = false,
}) => {
  const nodeHeading = `Canvas ${node.is_deleted ? "(Deleted)" : nodeNumber}`;
  const breadcrumbStepNumber =
    parentContext?.globalStepNumber != null
      ? parentContext.globalStepNumber
      : typeof parentContext?.stepIndex === "number"
        ? parentContext.stepIndex + 1
        : "";
  const [canvasData, setCanvasData] = useState(null);

  // Get revisionId and authUser for ping API
  const location = useLocation();
  const params = useParams();
  const authUser = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  // Extract revisionId from params (could be revisionId or revision_id depending on route)
  const revisionId = params.revisionId || params.revision_id;

  // Check if we're on a view-revision route
  const isViewRevisionRoute =
    location.pathname.includes("/view-revision") ||
    location.pathname.includes("/view-section") ||
    location.pathname.includes("/view-step") ||
    location.pathname.includes("/view-substep") ||
    location.pathname.includes("/view-childnode");

  // Start ping interval if on view-revision route
  useEffect(() => {
    if (!isViewRevisionRoute || !revisionId || !authUser) return;

    const interval = setInterval(async () => {
      try {
        const pingData = {
          task_id: revisionId,
          user_id: authUser.id,
        };
        await pingTask(pingData);
      } catch (error) {
        console.error("Ping failed in CanvasNode:", error);
      }
    }, 60000); // 60 seconds = 1 minute

    return () => {
      clearInterval(interval);
    };
  }, [isViewRevisionRoute, revisionId, authUser]);

  useEffect(() => {
    // Always fetch and process when we have canvas_json_data_file so textboxes show
    // latest part details (current_canvas_data_json). Only use snapshots as fallback when fetch fails.
    const fetchAndProcessData = async () => {
      if (!node.detail?.canvas_json_data_file) {
        setCanvasData(null);
        return;
      }

      try {
        const response = await fetch(node.detail.canvas_json_data_file, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          mode: "cors",
          credentials: "omit",
          cache: "no-cache",
        });

        if (!response.ok) {
          throw new Error("Failed to fetch canvas data");
        }

        const data = await response.json();

  
        const sourceData = node.detail?.current_canvas_data_json;

        let processedPages = processCanvasJsonWithPartDetails(
          data,
          sourceData,
        );
        const sourcePartNumbers = Array.isArray(sourceData?.part_details)
          ? sourceData.part_details
              .map((part) => part?.part_number)
              .filter(Boolean)
          : [];
        processedPages = await refreshCanvasPagesWithLatestParts(processedPages, {
          extraPartNumbers: sourcePartNumbers,
          dispatch,
        });

        if (Array.isArray(data)) {
          setCanvasData(processedPages);
        } else if (data?.results?.pages) {
          setCanvasData({
            ...data,
            results: { ...data.results, pages: processedPages },
          });
        } else if (data?.pages) {
          setCanvasData({ ...data, pages: processedPages });
        } else {
          setCanvasData(processedPages);
        }
      } catch (error) {
        console.error("Error fetching or processing canvas data:", error);
        // Fallback to null so renderer might handle it or show error
        setCanvasData(null);
      }
    };

    fetchAndProcessData();
  }, [node.detail?.canvas_json_data_file, isLocked, node.detail]);

  // Determine checkbox state for this node
  // Audit preview uses procedure `tree_node_id`; detail may carry `canvas_tree_node_id` — match any stable id.
  const canvasNodeId = node.detail?.canvas_tree_node_id || node.tree_node_id;
  const canvasIdMatches = (id) =>
    id != null &&
    (id === node.tree_node_id ||
      id === node.detail?.canvas_tree_node_id ||
      id === node.detail?.canva_tree_node_id ||
      id === node.detail?.procedure_tree_ref);
  const isCurrent =
    checkboxState?.currentNodeId != null &&
    canvasIdMatches(checkboxState.currentNodeId) &&
    (checkboxState?.currentNodeType === "canva" ||
      checkboxState?.currentNodeType === nodeType);
  const isSourceCanvas =
    checkboxState?.sourceNodeId != null &&
    canvasIdMatches(checkboxState.sourceNodeId);
  const isChecked =
    isCurrent || checkboxState?.allChildrenChecked || isSourceCanvas;
  const isDisabled =
    disabled ||
    (checkboxState?.allChildrenDisabled && !isCurrent) ||
    isRevisionLocked;
  const isDescendantLocked = isDescendantLockedPreview(
    checkboxState,
    isCurrent,
    isChecked,
    isSourceCanvas,
  );
  const isAuditPreviewFocusedLeaf = isAuditPreviewFocusedLeafCheckbox(
    checkboxState,
    isCurrent,
    isChecked,
    isSourceCanvas,
  );
  const isPreviewLockedLeafStyle = isDescendantLocked || isAuditPreviewFocusedLeaf;
  const popupTitle = [
    parentContext?.documentType,
    parentContext?.documentPartNumber,
    parentContext?.documentRevision
      ? `Rev ${parentContext.documentRevision}`
      : "",
    parentContext?.substepLetter
      ? `Substep ${breadcrumbStepNumber}${parentContext.substepLetter}`
      : breadcrumbStepNumber
        ? `Step ${breadcrumbStepNumber}`
        : "",
    nodeHeading,
  ]
    .filter(Boolean)
    .join(" ");
  const openStageInNewWindow = useCallback(
    (payload) => {
      if (!enableStagePopup) return;
      const storageKey = `canvas_stage_preview_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 11)}`;
      try {
        sessionStorage.setItem(
          storageKey,
          JSON.stringify({ ...payload, title: popupTitle }),
        );
      } catch (err) {
        console.error("Canvas preview could not be stored (data too large?)", err);
        return;
      }
      const pathSegments = window.location.pathname.split("/").filter(Boolean);
      const basename = pathSegments[0] ? `/${pathSegments[0]}` : "";
      const url = `${window.location.origin}${basename}/canvas-stage-preview?key=${encodeURIComponent(
        storageKey,
      )}`;
      const features =
        "width=840,height=640,left=72,top=72,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes";
      const win = window.open(url, "_blank", features);
      if (win) win.opener = null;
    },
    [enableStagePopup, popupTitle],
  );

  return (
    <div
      key={canvasNodeId}
      data-export-block="canvas"
      style={{ marginTop: showCheckbox ? "40px" : "30px", clear: "both" }}
    >
      {/* Show heading - use showHeading if provided, otherwise fall back to showCheckbox */}
      {(showHeading !== null ? showHeading : showCheckbox) && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            {showCheckbox && (
              <div
                style={{
                  position: "relative",
                  display: "inline-block",
                  ...auditPreviewSectionDescendantWrapStyle(checkboxState),
                }}
              >
                {isDescendantLocked ? (
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled
                    readOnly
                    tabIndex={-1}
                    aria-readonly="true"
                    onChange={() => {}}
                    style={getDescendantLockedCheckboxStyle()}
                  />
                ) : isAuditPreviewFocusedLeaf ? (
                  <div style={getHaloWrapperStyle()}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      tabIndex={-1}
                      aria-readonly="true"
                      onChange={() => {}}
                      style={getAuditPreviewReadonlyHaloCheckboxStyle()}
                    />
                  </div>
                ) : (
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={isDisabled}
                  onChange={() => {
                    // Prevent action for source canvas being moved
                    if (isSourceCanvas) {
                      return;
                    }
                    if (!isDisabled && onNavigate) {
                      onNavigate(nodeType || "canva", node, {
                        ...parentContext,
                        nodeNumber,
                      });
                    }
                  }}
                  style={{
                    cursor: isDisabled
                      ? "not-allowed"
                      : isSourceCanvas
                        ? "not-allowed"
                        : "pointer",
                    accentColor: isSourceCanvas
                      ? "rgb(68, 68, 68)"
                      : isChecked
                        ? NODE_SELECTION_BLUE
                        : undefined,
                    backgroundColor: isSourceCanvas
                      ? "rgb(68, 68, 68)"
                      : undefined,
                  }}
                />
                )}
                {/* Red cross icon overlay - shown when disabled and hovering */}
                {isDisabled && !isPreviewLockedLeafStyle && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      opacity: 0,
                      transition: "opacity 0.2s ease-in-out",
                      pointerEvents: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.opacity = "1";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.opacity = "0";
                    }}
                    className="checkbox-hover-overlay"
                  >
                    <FontAwesomeIcon
                      icon={faTimes}
                      size="sm"
                      color="red"
                      style={{
                        filter: "drop-shadow(0 0 1px rgba(255,255,255,0.8))",
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            <h2
              onClick={() => {
                // Prevent action for source canvas being moved
                if (isSourceCanvas) {
                  return;
                }
                if (isPreviewLockedLeafStyle) return;
                if (!isDisabled && showCheckbox && onNavigate) {
                  onNavigate(nodeType || "canva", node, {
                    ...parentContext,
                    nodeNumber,
                  });
                }
              }}
              style={{
                fontSize: "20px",
                margin: 0,
                fontWeight: isPreviewLockedLeafStyle ? 700 : undefined,
                cursor: isSourceCanvas
                  ? "not-allowed"
                  : isPreviewLockedLeafStyle
                    ? "default"
                  : !isDisabled && showCheckbox
                    ? "pointer"
                    : "default",
                color: isPreviewLockedLeafStyle
                  ? "#111"
                  : showCheckbox
                    ? checkboxLabelColor || undefined
                    : undefined,
                userSelect: "none",
              }}
            >
              {nodeHeading}
            </h2>
          </div>

          <div
            style={{
              color: "#888",
              fontSize: "14px",
              fontWeight: "normal",
            }}
          >
            {!node.is_deleted &&
              (parentContext?.auditSubstepBreadcrumbFull ? (
                <>{parentContext.auditSubstepBreadcrumbFull}</>
              ) : (
                <>
                  Section {parentContext.sectionIndex + 1} &gt; Step{" "}
                  {breadcrumbStepNumber}
                  {parentContext.substepLetter && (
                    <>
                      {" "}
                      &gt; Substep {breadcrumbStepNumber}
                      {parentContext.substepLetter}
                    </>
                  )}
                </>
              ))}
          </div>
        </div>
      )}

       {(() => {
                      const rawShort = node.detail?.short_description || "";
                      // Convert plain text with quotes to HTML format for proper rendering
                      const contentHtml =
                        typeof rawShort === "string" && rawShort.includes('class="editor-shell"')
                          ? rawShort.replace(/^<div class="editor-shell">(.*)<\/div>\s*$/s, "$1")
                          : rawShort && rawShort.trim() !== ""
                            ? rawShort.includes('<') && rawShort.includes('>')
                              ? rawShort // Already HTML, use as-is
                              : `<p>${rawShort.replace(/"/g, '&quot;')}</p>` // Plain text, wrap in p tags
                            : rawShort;
                      return contentHtml ? (
                        <div style={{ marginTop: "5px" }}>
                          <div className="rich-text-content-display">
                            <Suspense fallback={<div>Loading...</div>}>
                              <LexicalComposer
                                initialConfig={{
                                  editorState: null,
                                  html: { import: buildImportMap() },
                                  namespace: "ShortDescriptionViewer",
                                  nodes: [...PlaygroundNodes],
                                  onError: (error) => {
                                    console.error("Error in Short Description viewer:", error);
                                  },
                                  theme: PlaygroundEditorTheme,
                                  readOnly: true,
                                  editable: false,
                                }}
                              >
                                <SharedHistoryContext>
                                  <TableContext>
                                    <ToolbarContext>
                                      <div className="editor-shell">
                                        <Editor
                                          handleEditorChange={() => {}}
                                          initialContent={contentHtml}
                                        />
                                      </div>
                                    </ToolbarContext>
                                  </TableContext>
                                </SharedHistoryContext>
                              </LexicalComposer>
                            </Suspense>
                          </div>
                        </div>
                      ) : null;
                    })()}

      {/* Canvas Preview - Prefer CanvasPageRenderer (latest part details) over snapshot images so textboxes show new details */}
      {(() => {
        const ctmData = node.detail?.ctm_data || [];
        const hasSnapshots =
          ctmData.length > 0 && ctmData.some((d) => d?.canva_snap_url);

        // Prefer processed data (with current_canvas_data_json) so textboxes show new details in Document view
        if (canvasData) {
          return (
            <div
              onClick={() => openStageInNewWindow({ type: "canvasData", canvasData })}
              style={{
                marginTop: "20px",
                width: "100%",
                overflow: "visible",
                cursor: enableStagePopup ? "zoom-in" : "default",
              }}
            >
              <CanvasPageRenderer
                canvasData={canvasData}
                width={Math.min(window.innerWidth * 0.85, 900)}
                showSnapshots={false}
              />
            </div>
          );
        }

        // Loading state if URL exists but data not yet loaded
        if (node.detail?.canvas_json_data_file) {
          return (
            <div style={{ marginTop: "20px", textAlign: "center" }}>
              Loading Canvas...
            </div>
          );
        }

        // Fallback: show snapshot images when no processed data (e.g. no JSON URL or fetch failed)
        if (hasSnapshots) {
          const sortedData = [...ctmData].sort((a, b) => {
            const dateA = a?.created_at;
            const dateB = b?.created_at;

            if (!dateA && !dateB) return 0;
            if (!dateA) return 1;
            if (!dateB) return -1;

            const timeA = new Date(dateA || 0).getTime();
            const timeB = new Date(dateB || 0).getTime();

            if (isNaN(timeA) && isNaN(timeB)) return 0;
            if (isNaN(timeA)) return 1;
            if (isNaN(timeB)) return -1;

            return timeA - timeB;
          });

          return (
            <div style={{ marginTop: "20px" }}>
              {sortedData.map(
                (ctmItem, index) =>
                  ctmItem?.canva_snap_url && (
                    <img
                      onClick={() =>
                        openStageInNewWindow({
                          type: "snapshot",
                          imageUrl: ctmItem.canva_snap_url,
                        })
                      }
                      key={`${
                        ctmItem.canvas_tree_node_id_ref ||
                        ctmItem.canvas_page_layout_ref ||
                        index
                      }-${index}`}
                      src={ctmItem.canva_snap_url}
                      alt={`Canvas snapshot ${index + 1}`}
                      style={{
                        maxWidth: "600px",
                        width: "100%",
                        height: "auto",
                        marginTop: "20px",
                        display: "block",
                        cursor: enableStagePopup ? "zoom-in" : "default",
                      }}
                    />
                  ),
              )}
            </div>
          );
        }

        return null;
      })()}

    </div>
  );
};

export default CanvasNode;
